import { countriesCodes } from '@/utils/constants';

export enum EControllingPersonTypes {
  shareholder = '1',
  other = '2',
  director = '3',
}

export enum EEmployeeType {
  leader = '1',
  employee = '2',
}

export enum EParentType {
  shareholder = 'shareholder',
  employee = 'employee',
  leader = 'leader',
}

export const titles = {
  M: 'M',
  MME: 'MME',
  MX: 'MX',
} as const;

export type TTitle = keyof typeof titles;

export enum EIncomeRange {
  LOW = '0-18',
  MEDIUM_LOW = '19-23',
  MEDIUM = '24-27',
  MEDIUM_HIGH = '28-35',
  HIGH = '36-56',
  HIGHEST = '57-*',
}

export enum EPersonalAssets {
  LOW = '0-2',
  MEDIUM_LOW = '3-22',
  MEDIUM = '23-128',
  MEDIUM_HIGH = '129-319',
  HIGH = '320-464',
  HIGHEST = '465-',
}

export enum ELegalAnnualTurnOver {
  LOW = '0-39',
  MEDIUM_LOW = '40-99',
  MEDIUM = '100-249',
  MEDIUM_HIGH = '250-999',
  HIGH = '1000-2999',
  BOTTOM_THREE_THOUSAND = '3000-9999',
  BOTTOM_TEN_THOUSAND = '10000-99999',
  BOTTOM_TEN_HUNDRED_THOUSAND = '100000-*',
}

export enum ELegalNetIncomeRange {
  LOW = '0-4',
  MEDIUM_LOW = '5-9',
  MEDIUM = '10-49',
  MEDIUM_HIGH = '50-149',
  HIGH = '150-499',
  HIGHEST = '500-*',
}

export enum ELegalNumberOfEmployeeRange {
  LOW = '0',
  MEDIUM_LOW = '1-9',
  MEDIUM = '10-99',
  MEDIUM_HIGH = '100-249',
  HIGH = '250-*',
}

export const UserStatus = {
  PENDING: 'PENDING',
  VALIDATED: 'VALIDATED',
  CANCELED: 'CANCELED',
} as const;

export type TUserStatus = keyof typeof UserStatus;

export const UserStatusLabel = { ...UserStatus, BLOCKED: 'BLOCKED' } as const;

export type TUserStatusLabel = keyof typeof UserStatusLabel;

export const statusColor: { [key in keyof typeof UserStatusLabel]: 'orange' | 'green' | 'red' | 'darkRed' } = {
  PENDING: 'orange',
  VALIDATED: 'green',
  CANCELED: 'red',
  BLOCKED: 'darkRed',
};

export type TUserCodeStatus =
  | 110001
  | 110002
  | 110003
  | 110004
  | 110005
  | 110006
  | 110007
  | 110008
  | 110009
  | 110010
  | 110012;

export enum EOccupationType {
  self_employed = 'self_employed',
  public_sector_employees = 'public_sector_employees',
  private_sector_employees = 'private_sector_employees',
  retired_people_and_students = 'retired_people_and_students',
  without_any_professional_activity = 'without_any_professional_activity',
}

export const entityType = {
  1: 'reportingFinancialInstitution',
  2: 'nonReportingFinancialInstitution',
  3: 'governmentalEntities',
  4: 'other',
  5: 'investmentEntity',
} as const;

export type TEntityTypeKeys = keyof typeof entityType;

export type TEncryptionPostUser = IPostUser & { passphrase?: string };

export const UserTypes = {
  naturalPerson: 1,
  business: 2,
  ngo: 3,
  government: 4,
} as const;

export type TUserTypesRecord = keyof typeof UserTypes;

export type TUserTypeId = (typeof UserTypes)[TUserTypesRecord];

export const legalSectorTypes = ['NAF', 'NACE'] as const;
export type TLegalSectorType = (typeof legalSectorTypes)[number];

export const sourceOfFunds = [
  'donation',
  'inheritance',
  'loan',
  'lottery',
  'pension',
  'proceeds_from_investment',
  'proceeds_from_sale',
  'salary',
  'savings',
] as const;
export type TSourceOfFunds = (typeof sourceOfFunds)[number];

export type TUserTypesKeys = keyof typeof UserTypes;

export const entitySanctionsQuestionnaire = [1, 2, 3, 4] as const;

export const personalAssetsValues = [0, 1, 2, 3, 4, 5, 6, 7] as const;
export type TPersonalAssets = (typeof personalAssetsValues)[number];

export const occupationCategoryValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;
export type TOccupationCategory = (typeof occupationCategoryValues)[number];

export const monthlyIncomeRangeValues = [1, 2, 3, 4, 5, 6, 7] as const;
export type TMonthlyIncomeRange = (typeof monthlyIncomeRangeValues)[number];

export type TUserSortKeys = keyof Pick<IUser, 'createdDate'>;
export interface ISearchUser {
  userId?: number;
  userTag?: string;
  email?: string;
  name?: string;
  legalName?: string;
  userTypeId?: number;
  createdDateFrom?: string;
  createdDateTo?: string;
  specifiedUSPerson?: number;
  userStatus?: string;
  pageCount?: number;
  sortBy?: TUserSortKeys;
  sortOrder?: 'ASC' | 'DESC';
}

export type TCountryCode = (typeof countriesCodes)[number];

export interface IPutUser {
  userTag?: string;
  specifiedUSPerson?: number; // 0	No, 1	Yes
  controllingPersonType?: number; //  0	None (default),1	Shareholder,2	Other_relationship,3	Director
  employeeType?: number; // 0	None (default),1	Leader,2	Employee
  userName?: string;
  title?: TTitle;
  firstname?: string;
  lastname?: string;
  middleNames?: string;
  birthday?: string;
  email?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country?: TCountryCode;
  secondaryAddress1?: string;
  secondaryAddress2?: string;
  secondaryAddress3?: string;
  secondaryPostcode?: string;
  secondaryCity?: string;
  secondaryState?: string;
  secondaryCountry?: TCountryCode;
  phone?: string;
  mobile?: string;
  nationality?: string;
  nationalityOther?: string;
  placeOfBirth?: string;
  birthCountry?: TCountryCode;
  occupation?: string;
  occupationType?: EOccupationType;
  incomeRange?: EIncomeRange; //  0-18, 19-23, 24-27, 28-35, 36-56, 57-*
  legalName?: string;
  legalRegistrationNumber?: string;
  legalTvaNumber?: string;
  legalRegistrationDate?: string;
  legalForm?: string; // See API for available values
  legalShareCapital?: number;
  legalSector?: string;
  legalSectorType?: TLegalSectorType | null;
  sourceOfFunds?: TSourceOfFunds | null;
  legalAnnualTurnOver?: ELegalAnnualTurnOver; // 0-39, 40-99, 100-249, 250-999, 1000-2999, 3000-9999, 10000-99999, 100000-*
  legalNetIncomeRange?: ELegalNetIncomeRange; // 0-4, 5-9, 10-49, 50-149, 150-499, 500-*
  legalNumberOfEmployeeRange?: ELegalNumberOfEmployeeRange; // 0, 1-9, 10-99, 100-249, 250-*
  effectiveBeneficiary?: number;
  language?: string;
  taxNumber?: string;
  taxResidence?: string;
  position?: string;
  personalAssets?: EPersonalAssets; // 0-2, 3-22, 23-128, 129-319, 320-464, 465-
  entityType?: number;
  isOnStockExchange?: number; // 0	No, 1	Yes
  entitySanctionsQuestionnaire?: number;
  activityOutsideEu?: number; // 0	No, 1	Yes
  economicSanctions?: number; // 0	No, 1	Yes
  residentCountriesSanctions?: number; // 0	No, 1	Yes
  involvedSanctions?: number; // 0	No, 1	Yes
  timezone?: string;
  isSelfEmployed?: boolean;
  sepaCreditorIdentifier?: string;
  distributionCountry?: TCountryCode;
  personalAssetsRange?: TPersonalAssets;
  monthlyIncomeRange?: TMonthlyIncomeRange;
  occupationCategory?: TOccupationCategory;
}

export interface IPostUser extends IPutUser {
  userTypeId?: number;
  parentUserId?: number;
  parentType?: string; // shareholder, employee, leader
}

export interface IUser extends IPostUser {
  userId: number;
  userStatus?: TUserStatus;
  countryName?: string;
  legalNameEmbossed?: string;
  kycLevel: number;
  kycReview: number;
  kycReviewComment?: string;
  isFreezed?: number;
  isFrozen?: number;
  IsLegalFrozen?: number;
  optInMailing?: number;
  sanctionsQuestionnaireDate?: string;
  createdDate?: string;
  modifiedDate?: string;
  walletCount?: number;
  payinCount?: number;
  totalRows?: number;
  children?: IUser[];
  codeStatus?: TUserCodeStatus;
}

export interface ISirenUserData
  extends Pick<
    IPutUser,
    'address1' | 'city' | 'country' | 'legalName' | 'legalRegistrationNumber' | 'legalTvaNumber' | 'phone' | 'postcode'
  > {
  activityType?: string;
  status?: 'A' | 'D' | 'N' | 'I' | 'C' | 'T' | 'S' | 'L' | 'O';
}

export const physicalUserRole = {
  legalRepresentative: 'legalRepresentative',
  shareholder: 'shareholder',
  legalRepresentativeAndShareholder: 'legalRepresentativeAndShareholder',
  employee: 'employee',
  selfEmployed: 'selfEmployed',
  minor: 'minor',
  child: 'child',
  anonymous: 'anonymous',
} as const;

export type TPhysicalUserRole = keyof typeof physicalUserRole;

export const legalFormsIT = {
  10001: 'Ditta individuale',
  10002: 'Società semplice (S.s.)',
  10003: 'Società in nome collettivo (S.n.c.)',
  10004: 'Società in accomandita semplice (S.a.s.)',
  10005: 'Società per azioni (S.p.A.)',
  10006: 'Società in accomandita per azioni (S.a.p.a.)',
  10007: 'Società a responsabilità limitata (S.r.l.)',
  10008: 'Società a responsabilità limitata semplificata (S.r.ls.)',
  10009: 'Società cooperativa (Coop.)',
  10010: 'Ente Pubblico Economico (E.P.E.)',
};

export const legalFormsDE = {
  20001: 'Selbstständiger',
  20002: 'Gesellschaft mit beschränkter Haftung Kapitalgesellschaft mit beschränkter Haftung der Gesellschafter',
  20003: 'Aktiengesellschaft Kapitalgesellschaft mit Aktien, börsennotiert oder privat',
  20004: 'Unternehmergesellschaft (haftungsbeschränkt) Sonderform der GmbH mit geringem Stammkapital Mini-GmbH',
  20005: 'Offene Handelsgesellschaft Personengesellschaft mit unbeschränkter Haftung der Gesellschafter',
  20006: 'Kommanditgesellschaft Personengesellschaft mit mindestens einem Vollhafter und einem Teilhafter',
  20007: 'GmbH & Co. Kommanditgesellschaft Mischform: GmbH als haftende Komplementärin in einer KG',
  20008: 'Eingetragener Kaufmann Einzelunternehmen mit Eintragung ins Handelsregister',
  20009: 'Eingetragene Genossenschaft Zusammenschluss zur Förderung der Mitgliederinteressen',
  20010: 'Partnerschaftsgesellschaft Personengesellschaft für Freiberufler',
  20011: 'Kommanditgesellschaft auf Aktien Mischform aus AG und KG',
  20012: 'Stiftung Vermögensgebundene Organisation mit gemeinnützigem oder privatem Zweck',
  20013: 'Eingetragener Verein Nichtwirtschaftlicher Zusammenschluss mit Eintragung',
  20014: 'Europäische Genossenschaft Genossenschaft nach EU-Recht mit Sitz in Deutschland',
  20015:
    'Partnergesellschaft mit beschränkter Berufshaftung / Personengesellschaft für Freiberufler (e.g. lawyers , doctors etc.)',
  20016: 'juristische Personen des öffentlichen Rechts',
  20017: 'Wohnungseigentümergesellschaft',
};

export const legalFormsES = {
  30001: 'Empresario Individual',
  30002: 'Sociedad civil',
  30003: 'Sociedad colectiva',
  30004: 'Sociedad Comanditaria Simple',
  30005: 'Sociedad Comanditaria por acciones',
  30006: 'Sociedad de responsabilidad limitada',
  30007: 'Sociedad de responsabilidad limitada unipersonal',
  30008: 'Sociedad Anónima',
  30009: 'Sociedad Anónima Unipersonal',
  30010: 'Sociedad Limitada Nueva Empresa',
  30011: 'Sociedad Limitada Laboral',
  30012: 'Sociedad Cooperativa',
  30013: 'Agrupación de Interés económico',
  30014: 'Sociedad de garantía recíproca',
  30015: 'Sociedad agraria de transformación',
  30016: 'Communidad de Bienes',
  30017: 'Fundación',
  30018: 'Asociaciones y otras instituciones sin fines de lucro a servicio de los hogares',
  30019: 'Entidad de derecho público',
  30020: 'Patrimonio de inversión sin personalidad jurídica',
};

export const legalForms = {
  1000: 'Entrepreneur Individuel a Responsabilité Limitée (EIRL)',
  2110: 'Indivision entre personnes physiques',
  2120: 'Indivision avec personne morale',
  2210: 'Société créée de fait entre personnes physiques',
  2220: 'Société créée de fait avec personne morale',
  2310: 'Société en participation entre personnes physiques',
  2320: 'Société en participation avec personne morale',
  2385: 'Société en participation de professions libérales',
  2400: 'Fiducie',
  2700: 'Paroisse hors zone concordataire',
  2800: 'Assujetti unique à la TVA',
  2900: 'Autre groupement de droit privé non doté de la personnalité morale',
  3110: "Représentation ou agence commerciale d'état ou organisme public étranger immatriculé au RCS",
  3120: 'Société étrangère immatriculée au RCS',
  3205: 'Organisation internationale',
  3210: 'Etat collectivité ou établissement public étranger',
  3220: 'Société étrangère non immatriculée au RCS',
  3290: 'Autre personne morale de droit étranger',
  4110: "Etablissement public national à caractère industriel ou commercial doté d'un comptable public",
  4120: "Etablissement public national à caractère industriel ou commercial non doté d'un comptable public",
  4130: 'Exploitant public',
  4140: 'Etablissement public local à caractère industriel ou commercial',
  4150: "Régie d'une collectivité locale à caractère industriel ou commercial",
  4160: 'Banque de France',
  5191: 'Société de caution mutuelle',
  5192: 'Société coopérative de banque populaire',
  5193: 'Caisse de crédit maritime mutuel',
  5194: 'Caisse (fédérale) de crédit mutuel',
  5195: 'fondation	Association coopérative inscrite (droit local Alsace Moselle)',
  5196: "Caisse d'épargne et de prévoyance à forme coopérative",
  5202: 'Société en nom collectif',
  5203: 'Société en nom collectif coopérative',
  5306: 'Société en commandite simple',
  5307: 'Société en commandite simple coopérative',
  5308: 'Société en commandite par actions',
  5309: 'Société en commandite par actions coopérative',
  5310: 'Société en libre partenariat (SLP)',
  5385: "Société d'exercice libéral en commandite par action",
  5410: 'SARL nationale',
  5415: "SARL d'économie mixte",
  5422: "SARL immobilière pour le commerce et l'industrie (SICOMI)",
  5426: 'Société immobilière de gestion',
  5430: 'Safer en SARL',
  5431: "SARL mixte d'intérêt agricole (SIMA)",
  5432: "SARL d'intérêt collectif agricole (SICA)",
  5442: "SARL d'attribution",
  5443: 'SARL coopérative de construction',
  5451: 'SARL coopérative de consommation',
  5453: 'SARL coopérative artisanale',
  5454: "SARL coopérative d'intérêt maritime",
  5455: 'SARL coopérative de transports',
  5458: 'SARL coopérative de production (SCOP)',
  5459: 'SARL union de sociétés coopératives',
  5460: 'SARL coopérative',
  5485: "Société d'exercice libéral à responsabilité limitée",
  5498: 'SARL unipersonnelle',
  5499: 'SARL',
  5505: "SA à participation ouvrière à conseil d'administration",
  5510: "SA nationale à conseil d'administration",
  5515: "SA d'économie mixte à conseil d'administration",
  5520: "Société d'investissement à capital variable (SICAV) à conseil d'administration",
  5522: "Société anonyme immobilière pour le commerce et l'industrie (SICOMI) à conseil d'administration",
  5525: "Société anonyme immobilière d'investissement à conseil d'administration",
  5530: "Safer anonyme à conseil d'administration",
  5531: "Société anonyme mixte d'intérêt agricole (SMIA) à conseil d'administration",
  5532: "Société anonyme mixte d'intérêt collectif agricole (SICA) à conseil d'administration",
  5542: "Société anonyme d'attribution à conseil d'administration",
  5543: "Société anonyme coopérative de construction à conseil d'administration",
  5546: "SA de HLM à conseil d'administration",
  5547: "SA coopérative de production de HLM à conseil d'administration",
  5548: "SA de crédit immobilier à conseil d'administration",
  5551: "SA coopérative de consommation à conseil d'administration",
  5552: "SA coopérative de commerçants détaillants à conseil d'administration",
  5553: "SA coopérative artisanale à conseil d'administration",
  5554: "SA coopérative (d'intérêt) maritime à conseil d'administration",
  5555: "SA coopérative de transports à conseil d'administration",
  5558: "SA coopérative de production (SCOP) à conseil d'administration",
  5559: "SA union de sociétés coopératives à conseil d'administration",
  5560: "SA coopérative à conseil d'administration",
  5585: "Société d'exercice libéral à forme anonyme à conseil d'administration",
  5599: "SA à conseil d'administration",
  5605: 'SA à participation ouvrière à directoire',
  5610: 'SA nationale à directoire',
  5615: "SA d'économie mixte à directoire",
  5620: 'SICAV',
  5622: 'SICOMI',
  5625: "Société immobilière d'investissement anonyme à directoire",
  5630: 'Safer anonyme à directoire',
  5631: "Société anonyme mixte d'intérêt agricole (SMIA)",
  5632: 'SICA',
  5642: "Société anonyme d'attribution à directoire",
  5643: 'Société anonyme coopérative de construction à directoire',
  5646: 'S.A. HLM à directoire',
  5647: 'Société coopérative de production de HLM anonyme à directoire',
  5648: 'SA de crédit immobilier à directoire',
  5651: 'SA coopérative de consommation à directoire',
  5652: 'SA coopérative de commerçants détaillants à directoire',
  5653: 'SA coopérative artisanale à directoire',
  5654: "SA coopérative (d'intérêt) maritime à directoire",
  5655: 'SA coopérative de transport à directoire',
  5658: 'SA coopérative de production (SCOP) à directoire',
  5659: 'SA union de sociétés coopératives à directoire',
  5660: 'SA coopérative à directoire',
  5685: "Société d'exercice libéral à forme anonyme à directoire",
  5699: '(Autre) SA à directoire',
  5710: 'SAS',
  5720: 'SASU',
  5785: "Société d'exercice libéral par action simplifiée",
  5800: 'Société européenne',
  6100: "Caisse d'épargne et de prévoyance",
  6210: 'GEIE',
  6220: 'GIE',
  6316: 'CUMA',
  6317: 'Société coopérative agricole',
  6318: 'Union de sociétés coopératives agricoles',
  6411: "Société d'assurance mutuelle",
  6521: 'SCPI',
  6532: 'SICA',
  6533: 'GAEC',
  6534: 'Groupement foncier agricole',
  6535: 'Groupement agricole foncier',
  6536: 'Groupement forestier',
  6537: 'Groupement pastoral',
  6538: 'Groupement foncier rural',
  6539: 'Société civile foncière',
  6540: 'SCI',
  6541: 'SCI de construction vente',
  6542: "Société civile d'attribution",
  6543: 'Société civile coopérative de construction',
  6544: "Société civile d'accession progressive à la propriété",
  6551: 'Société civile coopérative de consommation',
  6554: "Société civile coopérative (d'intérêt) maritime",
  6558: 'Société civile coopérative entre médecins',
  6560: 'SCP',
  6561: "SCP d'avocats",
  6562: "SCP d'avocats aux conseil",
  6563: "SCP d'avoués d'appel",
  6564: "SCP d'huissiers",
  6565: 'SCP de notaires',
  6566: 'SCP de commissaires-priseurs',
  6567: 'SCP de greffiers de tribunal de commerce',
  6568: 'SCP de conseils juridiques',
  6569: 'SCP de commissaires aux comptes',
  6571: 'SCP de médecins',
  6572: 'SCP de dentistes',
  6573: "SCP d'infirmiers",
  6574: 'SCP de masseurs kinésithérapeutes',
  6575: "SCP de directeurs de laboratoire d'analyse médicale",
  6576: 'SCP de vétérinaires',
  6577: 'SCP de géomètres-experts',
  6578: "SCP d'architectes",
  6585: 'SCP',
  6588: 'Société civile laitière',
  6589: 'Société civile de moyens',
  6595: 'Caisse (locale) de crédit mutuel',
  6596: 'Caisse de crédit agricole mutuel',
  6597: "Société civile d'exploitation agricole",
  6598: 'rator	Exploitation agricole à responsabilité limitée',
  6599: 'Autre société civile',
  6901: 'Autres personnes de droit privé inscrites au registre du commerce et des sociétés',
  7111: 'Autorité constitutionnelle',
  7112: 'Autorité administrative indépendante',
  7113: 'Ministère',
  7120: "Service central d'un ministère",
  7150: 'Service du ministère de la Défense',
  7160: "Service déconcentré à compétence nation . D'un ministère (hors Défense)",
  7171: "Service déconcentré de l'Etat à compétence (inter) régionale",
  7172: "Service déconcentré de l'Etat à compétence (inter) départementale",
  7179: "(Autre) Service déconcentré de l'Etat à compétence territoriale",
  7190: 'Ecole nationale non dotée de la personnalité morale',
  7210: 'Commune',
  7220: 'Département',
  7225: "Territoire d'Outre-Mer",
  7229: '(Autre) Collectivité territoriale',
  7230: 'Région',
  7312: 'Commune associée',
  7313: 'Section de commune',
  7314: 'Ensemble urbain',
  7321: 'Association syndicale autorisée',
  7322: 'Association foncière urbaine',
  7323: 'Association foncière de remembrement',
  7331: "Etablissement public local d'enseignement",
  7340: 'Pôle métropolitain',
  7341: 'Secteur de commune',
  7342: 'District urbain',
  7343: 'Communauté urbaine',
  7344: 'Métropole',
  7345: 'Syndicat intercommunal à vocation multiple (SIVOM)',
  7346: 'Communauté de commune',
  7347: 'Communauté de villes',
  7348: "Communauté d'agglomération",
  7349: 'Autre établissement public local de coopération non spécialisé ou entente',
  7351: 'Institution interdépartementale ou entente',
  7352: 'Institution interrégionale ou entente',
  7353: 'Syndicat intercommunal à vocation unique (SIVU)',
  7354: 'Syndicat mixte communal',
  7355: 'Autre syndicat mixte',
  7356: 'Commission syndicale pour la gestion des biens indivis des communes',
  7361: "Centre communal d'action sociale",
  7362: 'Caisse des écoles',
  7363: 'Caisse de crédit municipal',
  7364: "Etablissement d'hospitalisation",
  7365: 'Syndicat inter hospitalier',
  7366: 'Etablissement public local social et médico-social',
  7371: "Office public d'habitation à loyer modéré (OPHLM)",
  7372: "Service départemental d'incendie",
  7373: 'Etablissement public local culturel',
  7378: "Régie d'une collectivité locale à caractère administratif",
  7379: '(Autre) Etablissement public administratif local',
  7381: 'Organisme consulaire',
  7382: "Etablissement public national ayant fonction d'administration centrale",
  7383: 'Etablissement public national à caractère scientifique culturel et professionnel',
  7384: "Autre établissement public national d'enseignement",
  7385: 'Autre établissement public national administratif à compétence territoriale limitée',
  7389: 'Etablissement public national à caractère administratif',
  7410: "Groupement d'intérêt public (GIP)",
  7430: "Etablissement public des cultes d'Alsace-Lorraine",
  7450: 'Etablissement public, cercle et foyer dans les armées',
  7470: 'Groupement de coopération sanitaire à gestion publique',
  7490: 'Autre personne morale de droit administratif',
  8110: 'Régime général de la sécurité sociale',
  8120: 'Régime spécial de sécurité sociale',
  8130: 'Institution de retraite complémentaire',
  8140: 'Mutualité sociale agricole',
  8150: 'Régime maladie des non-salariés non agricoles',
  8160: 'Régime vieillesse ne dépendant pas du régime général de la sécurité sociale',
  8170: "Régime d'assurance chômage",
  8190: 'Autre régime de prévoyance sociale',
  8210: 'Mutuelle',
  8250: 'Assurance mutuelle agricole',
  8290: 'Autre organisme mutualiste',
  8310: "Comité social économique d'entreprise",
  8311: "Comité social économique d'établissement",
  8410: 'Syndicat de salariés',
  8420: 'Syndicat patronal',
  8450: 'Ordre professionnel ou assimilé',
  8470: 'Centre technique industriel ou comité professionnel du développement économique',
  8490: 'Autre organisme professionnel',
  8510: 'Institution de prévoyance',
  8520: 'Institution de retraite supplémentaire',
  9110: 'Syndicat de copropriété',
  9150: 'Association syndicale libre',
  9210: 'Association non déclarée',
  9220: 'Association déclarée',
  9221: "Association déclarée 'entreprises d'insertion par l'économique'",
  9222: 'Association intermédiaire',
  9223: "Groupement d'employeurs",
  9224: "Association d'avocats à responsabilité professionnelle individuelle",
  9230: "Association déclarée reconnue d'utilité publique",
  9240: 'Congrégation',
  9260: 'Association de droit local',
  9300: 'Fondation',
  9900: 'Autre personne morale de droit privé',
  9970: 'Groupement de coopération sanitaire à gestion privée',
  ...legalFormsDE,
  ...legalFormsES,
  ...legalFormsIT,
};

export type TLegalFormKey = keyof typeof legalForms;

export const selfEmployedLegalForms: TLegalFormKey[] = [1000, 10001, 20001, 30001];

export const legalFormCountries: TCountryCode[] = ['IT', 'ES', 'DE'];
